import React, { useState, useEffect, useRef } from 'react';
import type { XYCoord } from 'react-dnd';
import { useDrop } from 'react-dnd';
import {
  Box,
  BoxProps,
  Button,
  HStack,
  IconButton,
  Image,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { DragItem, ItemTypes } from '../dnd/types';
import { DragBox } from '../dnd/DragBox';
import { DragLayer } from '../dnd/DragLayer';
import { dirFromDndPos, flexDirFromDndPos, getCoords } from '../shared/utils';
import { RefreshIcon } from '../../../../../../components/icons';

type Point = {
  x: string | number;
  y: string | number;
};

type Spot = {
  key: string;
  type: 'Rectangle' | 'Circle' | 'Shape';
  point?: Point;
  radius?: string | number;
  points?: Point[];
  answer: string;
  caseSensitive?: string;
  showArea?: boolean;
};

interface ImageHotspotsDndProps extends BoxProps {
  src: string;
  spots: Spot[];
  debug?: boolean;
  showCorrect?: boolean;
  dndPosition?: 'left' | 'top' | 'right' | 'bottom' | 'string';
  isViewOnly?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: ContainerState['state'];
}

export interface ContainerState {
  state: {
    [key: string]: {
      top?: number | string;
      left?: number | string;
      text: string;
      correct: boolean;
    };
  };
}

const ImageHotspotsDnd: React.FC<ImageHotspotsDndProps> = ({
  src,
  spots,
  debug,
  showCorrect,
  dndPosition = 'right',
  isViewOnly,
  submit,
  reset,
  initialState,
  ...props
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [state, updateState] = useState<ContainerState['state']>({});
  const [showCorrectAnswers, setShowCorrectAnswers] = useState<boolean>();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const { t } = useTranslation();

  const moveBox = (
    id: string,
    left: number | string,
    top: number | string,
    width: number,
    height: number,
    reset?: boolean,
  ) => {
    const newState = { ...state };
    const spot = spots.find((s) => s.key === id);
    let correct = false;
    if (reset) {
      newState[id] = {
        text: newState[id]!.text,
        correct,
        left: undefined,
        top: undefined,
      };
      updateState(newState);
      return;
    }
    if (spot) {
      const leftNumber = parseFloat('' + left) + width / 2;
      const topNumber = parseFloat('' + top) + height / 2;
      const topLeftPoint = spot.points![0]!;
      const bottomRightPoint = spot.points![1]!;
      const topLeftPointLeft = parseFloat('' + topLeftPoint.x) * 0.8;
      const topLeftPointTop = parseFloat('' + topLeftPoint.y);
      const bottomRightPointLeft = parseFloat('' + bottomRightPoint.x) * 0.8;
      const bottomRightPointTop = parseFloat('' + bottomRightPoint.y);
      if (
        leftNumber >= topLeftPointLeft &&
        leftNumber <= bottomRightPointLeft &&
        topNumber >= topLeftPointTop &&
        topNumber <= bottomRightPointTop
      ) {
        correct = true;
      }
    }

    newState[id] = { text: newState[id]!.text, correct, left, top };
    updateState(newState);
  };

  const [{ hovering }, dropRef] = useDrop(
    () => ({
      accept: ItemTypes.DragBox,
      drop(item: DragItem, monitor) {
        const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;

        if (rootRef.current) {
          // const percentageDeltaX =
          //   (delta.x / rootRef.current.clientWidth) * 100;
          // const percentageDeltaY =
          //   (delta.y / rootRef.current.clientHeight) * 100;
          const width = (item.width / rootRef.current.clientWidth) * 100;
          const height = (item.height / rootRef.current.clientHeight) * 100;

          const parent = getCoords(rootRef.current);
          const child = getCoords(item.el);

          const left = `${
            ((child.left - parent.left + delta.x) /
              rootRef.current.clientWidth) *
            100
          }%`;
          const top = `${
            ((child.top - parent.top + delta.y) /
              rootRef.current.clientHeight) *
            100
          }%`;
          moveBox(item.id, left, top, width, height);
        }
        return undefined;
      },
      collect: (monitor) => ({
        hovering: monitor.isOver(),
      }),
    }),
    [moveBox],
  );

  const [, dropRef2] = useDrop(
    () => ({
      accept: ItemTypes.DragBox,
      drop(item: DragItem, monitor) {
        const delta = monitor.getDifferenceFromInitialOffset() as XYCoord;

        if (rootRef.current) {
          const width = (item.width / rootRef.current.clientWidth) * 100;
          const height = (item.height / rootRef.current.clientHeight) * 100;

          const parent = getCoords(rootRef.current);
          const child = getCoords(item.el);

          const left = `${
            ((child.left - parent.left + delta.x) /
              rootRef.current.clientWidth) *
            100
          }%`;
          const top = `${
            ((child.top - parent.top + delta.y) /
              rootRef.current.clientHeight) *
            100
          }%`;

          moveBox(item.id, left, top, width, height, true);
        }
        return undefined;
      },
    }),
    [moveBox],
  );

  const setInitialState = () => {
    const state: ContainerState['state'] = {};

    spots.forEach((spot, index) => {
      state[spot.key] = {
        text: spot.answer,
        correct: false,
      };
    });

    updateState(state);
  };

  useEffect(() => {
    if (initialState) {
      updateState(initialState);
      setShowResults(true);
      let correct = 0;

      Object.keys(initialState).forEach((key) => {
        if (initialState[key]!.correct) {
          correct += 1;
        }
      });
      setShownCorrect(correct);

      setShownCorrect(correct);
    } else setInitialState();
  }, [spots, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state).forEach((key) => {
      if (state[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, spots.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    if (reset != null) reset();
  };

  const ret = (
    <VStack w="full" spacing={0} gap={4}>
      <Stack
        w="full"
        flexDir={flexDirFromDndPos(dndPosition)}
        {...props}
        ref={(el) => {
          dropRef(el);
          rootRef.current = el;
        }}
        position="relative"
      >
        <Image src={src} w="80%" h="auto" />
        {spots.map((spot) => {
          if (debug || spot.showArea || showCorrect)
            return (
              <Box
                position="absolute"
                left={`${parseFloat(String(spot.points![0]!.x)) * 0.8}%`}
                top={spot.points![0]!.y}
                right={`${100 - parseFloat(String(spot.points![1]!.x)) * 0.8}%`}
                bottom={`${100 - parseFloat(String(spot.points![1]!.y))}%`}
                backgroundColor="rgba(16,46,142,0.15)"
              />
            );
          return null;
        })}
        <VStack
          spacing={0}
          gap={4}
          flexGrow={1}
          m="0 auto"
          position="static"
          background={hovering ? 'rgba(208,208,208,0.45)' : undefined}
          borderRadius="8px"
          direction={dirFromDndPos(dndPosition)}
          w={dirFromDndPos(dndPosition) === 'row' ? 'full' : undefined}
          minH={dirFromDndPos(dndPosition) === 'row' ? '40px' : undefined}
          minW={dirFromDndPos(dndPosition) === 'column' ? '120px' : undefined}
          align="center"
          alignSelf={
            dirFromDndPos(dndPosition) === 'column' ? 'stretch' : undefined
          }
          ref={dropRef2}
        >
          <DragLayer />
          {/* So the stack streches */}
          <div></div>
          {Object.keys(state).map((key) => {
            const { left, top, text } = state[key] as {
              top?: number;
              left?: number;
              text: string;
            };
            return (
              <DragBox
                key={key}
                id={key}
                left={left}
                top={top}
                text={text}
                boxShadow="md"
              />
            );
          })}
        </VStack>
      </Stack>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {showResults && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                {t('general.reset')}
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {spots.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              {t('general.submit')}
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );

  if (debug) {
    return (
      <VStack>
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default ImageHotspotsDnd;
